/**
 * Загрузка manifest.json — контракта между конвейером данных и фронтендом.
 *
 * Фронтенд не знает заранее, какие слои опубликованы: админка может выложить
 * новую версию слоя или добавить ещё один, и карта обязана подхватить это без
 * пересборки. Поэтому весь список источников, их зумы и границы приходят из
 * манифеста, а не зашиты в код.
 */

export interface SourceLayerInfo {
  id: string;
  minzoom: number | null;
  maxzoom: number | null;
  fields: string[];
}

export interface TileSourceInfo {
  url: string;
  sizeBytes: number;
  minzoom: number;
  maxzoom: number;
  /** [minLon, minLat, maxLon, maxLat] */
  bounds: [number, number, number, number];
  /** [lon, lat, zoom] */
  center: [number, number, number];
  tileType: string;
  sourceLayers: SourceLayerInfo[];
}

export interface Manifest {
  generatedAt: string;
  attribution: string;
  fonts: { url: string; families: string[] };
  sprites: { url: string; available: string[] };
  search: { indexes: string[] };
  /** Легенды растровых листов: имя источника -> адрес картинки. */
  legends?: Record<string, string>;
  sources: Record<string, TileSourceInfo>;
}

export class ManifestError extends Error {
  constructor(
    message: string,
    readonly hint: string,
  ) {
    super(message);
    this.name = "ManifestError";
  }
}

const MANIFEST_URL = "/manifest.json";

export async function loadManifest(signal?: AbortSignal): Promise<Manifest> {
  let response: Response;
  try {
    response = await fetch(MANIFEST_URL, {
      cache: "no-cache",
      ...(signal ? { signal } : {}),
    });
  } catch {
    throw new ManifestError(
      "Не удалось связаться с сервером карты.",
      "Проверьте подключение к сети. Если сервер запускается локально — выполните «npm run serve».",
    );
  }

  if (!response.ok) {
    throw new ManifestError(
      `Сервер вернул ${response.status} при запросе манифеста слоёв.`,
      "Похоже, тайлы ещё не собраны. Выполните «npm run tiles:basemap».",
    );
  }

  let manifest: Manifest;
  try {
    manifest = (await response.json()) as Manifest;
  } catch {
    throw new ManifestError(
      "Манифест слоёв повреждён.",
      "Пересоберите его командой «npm run tiles:validate».",
    );
  }

  if (!manifest.sources || Object.keys(manifest.sources).length === 0) {
    throw new ManifestError(
      "В манифесте нет ни одного источника тайлов.",
      "Соберите базовую подложку: «npm run data:osm» и «npm run tiles:basemap».",
    );
  }

  return manifest;
}

/** Абсолютный URL архива для протокола pmtiles:// — относительные пути он не принимает. */
export function absoluteTileUrl(source: TileSourceInfo): string {
  return new URL(source.url, window.location.origin).toString();
}

/** Есть ли в источнике слой с таким именем (source-layer в терминах MapLibre). */
export function hasSourceLayer(source: TileSourceInfo, layerId: string): boolean {
  return source.sourceLayers.some((layer) => layer.id === layerId);
}

/**
 * Тематические слои — это всё, кроме базовой подложки. Имя "basemap"
 * закреплено конвейером (scripts/build-basemap.sh).
 */
export const BASEMAP_SOURCE_ID = "basemap";

export function thematicSources(manifest: Manifest): [string, TileSourceInfo][] {
  return Object.entries(manifest.sources).filter(([id]) => id !== BASEMAP_SOURCE_ID);
}

/** Растровый источник: лист генплана картинкой, без атрибутов и кликов. */
export function isRaster(source: TileSourceInfo): boolean {
  return source.tileType !== "mvt";
}
